import Modal from '@src/components/common/Modal';
import {
  hasUploadFailed,
  isStoryEditorEntryReady,
} from '@src/components/Dashboard/utils';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import {
  useDashboard,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import type { DashboardStory } from '@src/types.ts/story';

interface StoryPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onConfirm: (storyId: string) => void;
}

export const StoryPickerModal = observer(
  ({ isOpen, onClose, title, onConfirm }: StoryPickerModalProps) => {
    const dashboardStore = useDashboard();
    const uploadFlowStore = useUserStoryUploadFlow();
    const [selectedStoryId, setSelectedStoryId] = useState<string | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useOutsideAlerter(dropdownRef, () => setDropdownOpen(false));

    const { homeStories, homeStoriesLoading } = dashboardStore;

    // PLAT-11224: stories are listed from upload time; still-processing ones
    // render disabled until the transcript is ready (quick actions need the
    // transcript, not the video renditions). Failed uploads stay hidden.
    const sortedStories = useMemo(
      () =>
        homeStories
          .filter(story => !hasUploadFailed(story))
          .sort((a, b) => (a.title || '').localeCompare(b.title || '')),
      [homeStories],
    );

    const filteredStories = useMemo(() => {
      if (!searchQuery.trim()) return sortedStories;
      const q = searchQuery.toLowerCase();
      return sortedStories.filter((s: DashboardStory) =>
        s.title?.toLowerCase().includes(q),
      );
    }, [sortedStories, searchQuery]);

    const selectedStory = sortedStories.find(
      (s: DashboardStory) => s.id === selectedStoryId,
    );

    const handleConfirm = () => {
      if (selectedStoryId) {
        onConfirm(selectedStoryId);
        setSelectedStoryId(null);
        setSearchQuery('');
      }
    };

    const handleClose = () => {
      setSelectedStoryId(null);
      setDropdownOpen(false);
      setSearchQuery('');
      onClose();
    };

    const handleDropdownToggle = () => {
      const opening = !dropdownOpen;
      setDropdownOpen(opening);
      if (opening) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      } else {
        setSearchQuery('');
      }
    };

    return (
      <Modal isOpen={isOpen} closeModal={handleClose}>
        <Wrapper data-testid="story-picker-modal">
          <Content>
            <Title>{title}</Title>
            <Section>
              <SectionSublabel>Select a Story</SectionSublabel>
              <StoryDropdown ref={dropdownRef}>
                <DropdownSelected onClick={handleDropdownToggle}>
                  <SelectedText>
                    {selectedStory?.title || 'Select a story...'}
                  </SelectedText>
                  <DropdownArrow>
                    <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
                      <path
                        d="M2 4.33008L6 8.33008L10 4.33008"
                        stroke="var(--accent)"
                      />
                    </svg>
                  </DropdownArrow>
                </DropdownSelected>
                {dropdownOpen && (
                  <DropdownOptions>
                    <SearchContainer>
                      <SearchInput
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search stories..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        onClick={e => e.stopPropagation()}
                      />
                    </SearchContainer>
                    <OptionsList>
                      {homeStoriesLoading && filteredStories.length === 0 ? (
                        <EmptyOption>Loading stories...</EmptyOption>
                      ) : filteredStories.length === 0 ? (
                        <EmptyOption>
                          {searchQuery
                            ? 'No stories match your search'
                            : 'No stories available'}
                        </EmptyOption>
                      ) : (
                        filteredStories.map((story: DashboardStory) => {
                          const ready = isStoryEditorEntryReady(
                            story,
                            uploadFlowStore.getClientVideoAnalysisProgress(
                              story.id,
                            ) !== undefined,
                          );
                          return (
                            <DropdownOption
                              key={story.id}
                              $disabled={!ready}
                              onClick={
                                ready
                                  ? () => {
                                      setSelectedStoryId(story.id);
                                      setDropdownOpen(false);
                                      setSearchQuery('');
                                    }
                                  : undefined
                              }
                            >
                              <OptionText>{story.title}</OptionText>
                              {!ready && (
                                <ProcessingHint>Processing…</ProcessingHint>
                              )}
                            </DropdownOption>
                          );
                        })
                      )}
                    </OptionsList>
                  </DropdownOptions>
                )}
              </StoryDropdown>
            </Section>
          </Content>
          <Footer>
            <WriteButton onClick={handleConfirm} disabled={!selectedStoryId}>
              Continue
            </WriteButton>
          </Footer>
        </Wrapper>
      </Modal>
    );
  },
);

const Wrapper = styled.div`
  border-radius: 10px;
  background-color: var(--surface-subtle);
  width: 100%;
  max-width: 672px;
  min-width: 512px;
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    min-width: unset;
  }
`;

const Content = styled.div`
  max-width: 636px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding: 0 24px 48px;
`;

const Title = styled.h2`
  margin: 0;
  padding-top: 40px;
  padding-bottom: 24px;
  color: var(--foreground);
  text-align: center;
  font-size: 32px;
  font-weight: 700;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionSublabel = styled.div`
  color: var(--foreground);
  font-size: 12px;
  line-height: 140%;
`;

const StoryDropdown = styled.div`
  position: relative;
`;

const DropdownSelected = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--surface);
  cursor: pointer;
  font-size: 12px;
  color: var(--foreground);
  transition:
    border-color 150ms ease,
    background-color 150ms ease;

  &:hover {
    background-color: var(--surface-elevated);
  }
`;

const SelectedText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownArrow = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const DropdownOptions = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--surface);
  box-shadow: var(--elevation-toast);
  z-index: 3;
  display: flex;
  flex-direction: column;
`;

const SearchContainer = styled.div`
  padding: 8px;
  border-bottom: 1px solid var(--border);
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background-color: var(--surface);
  color: var(--foreground);
  font-size: 12px;
  outline: none;
  box-sizing: border-box;
  transition: border-color var(--transition-fast);

  &::placeholder {
    color: var(--text-muted);
  }

  &:hover {
    border-color: var(--text-muted);
  }

  &:focus {
    border-color: var(--accent);
  }
`;

const OptionsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: thin;
`;

const DropdownOption = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  font-size: 12px;
  color: ${({ $disabled }) =>
    $disabled ? 'var(--text-muted)' : 'var(--foreground)'};
  transition: background-color 150ms ease;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
  &:hover {
    background-color: ${({ $disabled }) =>
      $disabled ? 'transparent' : 'rgba(242, 208, 147, 0.08)'};
  }
  &:first-child {
    border-radius: 0;
  }
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
  &:only-child {
    border-radius: 0 0 8px 8px;
  }
`;

const OptionText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProcessingHint = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  color: var(--text-muted);
`;

const EmptyOption = styled.div`
  padding: 12px 10px;
  font-size: 12px;
  color: var(--text-muted);
  text-align: center;
`;

const Footer = styled.div`
  padding: 24px 0 16px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -40px;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      var(--surface-subtle) 100%
    );
    pointer-events: none;
  }
`;

const WriteButton = styled.button`
  width: 240px;
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  background: var(--primary);
  border-radius: 8px;
  border: none;
  outline: none;
  cursor: pointer;
  color: var(--background);
  font-size: 14px;
  font-weight: 700;
  transition: 0.15s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
